import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../api';
import { getImageUrl } from '../../utils/imageUtils';

export type StoreBrand = { name: string; logo: string };

export function useStoreBrand(): {
  brand: StoreBrand;
  logoFailed: boolean;
  setLogoFailed: (v: boolean) => void;
} {
  const [brand, setBrand] = useState<StoreBrand>({ name: 'NLS Store', logo: '' });
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/admin/ultra-settings/public/store`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && data.data) {
          setLogoFailed(false);
          setBrand({
            name: data.data.storeName || 'NLS Store',
            logo: getImageUrl(data.data.storeLogo || ''),
          });
        }
      })
      .catch(() => undefined);
  }, []);

  return { brand, logoFailed, setLogoFailed };
}

type AdminBrandLogoProps = {
  brand: StoreBrand;
  logoFailed: boolean;
  onLogoError: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  linkTo?: string;
  /** Light logo container on dark sidebar */
  onDark?: boolean;
};

const heightClass = { sm: 'h-8', md: 'h-10', lg: 'h-11' };
const maxWClass = { sm: 'max-w-[120px]', md: 'max-w-[180px]', lg: 'max-w-[200px]' };

export const AdminBrandLogo: React.FC<AdminBrandLogoProps> = ({
  brand,
  logoFailed,
  onLogoError,
  size = 'md',
  className = '',
  linkTo,
  onDark = false,
}) => {
  const showLogo = Boolean(brand.logo) && !logoFailed;

  const inner = showLogo ? (
    <img
      src={brand.logo}
      alt={brand.name}
      className={`${heightClass[size]} w-auto ${maxWClass[size]} object-contain object-left ${className}`}
      onError={onLogoError}
    />
  ) : (
    <span
      className={`inline-flex ${heightClass[size]} min-w-[2rem] items-center justify-center rounded-lg bg-primary-600 px-2 text-sm font-bold text-white ${className}`}
    >
      {brand.name.charAt(0).toUpperCase()}
    </span>
  );

  const wrapped =
    onDark && showLogo ? (
      <span className="inline-flex rounded-lg bg-white px-2.5 py-1.5 shadow-sm">{inner}</span>
    ) : (
      inner
    );

  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex shrink-0 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 rounded-md">
        {wrapped}
      </Link>
    );
  }

  return <span className="inline-flex shrink-0 items-center">{wrapped}</span>;
};
